/*
 * Copyright (c) 2026. Tooling Team
 */

import Layout from "../components/layout/Layout";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { useLocation } from "react-router-dom";
import useMedDomainDiff from "../hooks/useMedDomainDiff";
import { ProgressBar } from "@uhg-abyss/web/ui/ProgressBar";
import {
  useCreateMedDomainDiffMutation,
  useGetMedDomainDiffStatusQuery,
} from "../app/services/medDomainApi";
import { Breadcrumbs } from "@uhg-abyss/web/ui/Breadcrumbs";
import { PATHS } from "../utils/constants";

export function MedDomainDiff() {
  const { search, pathname } = useLocation();
  const currentUrl = `${pathname}${search || ""}`;
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const groupId = searchParams.get("groupId");
  const newBuildId = searchParams.get("newBuildId");
  const oldBuildId = searchParams.get("oldBuildId");

  const [reloadKey, setReloadKey] = useState(0);
  const {
    state: { medDomainBuildDiffData, rawDiffResponse },
  } = useMedDomainDiff({ groupId, newBuildId, oldBuildId, reloadKey });

  const [createdDiffBuildId, setCreatedDiffBuildId] = useState(null);
  const [diffStatus, setDiffStatus] = useState(null);
  const [createMedDomainDiff] = useCreateMedDomainDiffMutation();

  const isPendingStatus =
    diffStatus === "WAITING" || diffStatus === "INPROGRESS";
  const isErrorStatus = diffStatus === "FAILED" || diffStatus === "DEPRECATED";

  const { data: diffStatusResponse, isFetching: isCheckingStatus } =
    useGetMedDomainDiffStatusQuery(createdDiffBuildId, {
      skip: !createdDiffBuildId,
      pollingInterval: isPendingStatus ? 60000 : 0, // poll every 60s while a diff job is pending
    });

  const handleCreateDiff = async () => {
    try {
      const result = await createMedDomainDiff({
        groupId,
        newBuildId,
        oldBuildId,
      }).unwrap();

      // Normal success path: result is a numeric diff_id
      if (typeof result === "number") {
        setCreatedDiffBuildId(result);
        return;
      }

      // Fallback: if we reach here, we didn't get a usable diff_id; log it
      console.error("Unexpected createMedDomainDiff response", result);
    } catch (e) {
      console.error("Error creating MedDomain diff", e);

      // Preserve existing fallback: if API says diff already INPROGRESS, extract diff_id
      if (e && Object.hasOwn(e, "detail")) {
        const detail = e.detail;
        const alreadyInProgressPhrase =
          "A diff is already INPROGRESS for MV name";

        if (detail.includes(alreadyInProgressPhrase)) {
          const match = detail.match(/diff_id=(\d+)/);
          if (match && match[1]) {
            const existingDiffId = Number(match[1]);
            if (!Number.isNaN(existingDiffId)) {
              setCreatedDiffBuildId(existingDiffId);
              return;
            }
          }
        }
      }
    }
  };

  // Detect "No Diff MV found" or capture existing diff_id based on rawDiffResponse
  //Todo: When BE has proper return for when a diff is running but not created yet, we can remove this.
  useEffect(() => {
    if (!rawDiffResponse || typeof rawDiffResponse !== "object") return;

    // If the response already carries a diff_id, track it for status polling
    if (
      Object.hasOwn(rawDiffResponse, "diff_id") &&
      typeof rawDiffResponse.diff_id === "number"
    ) {
      setCreatedDiffBuildId(rawDiffResponse.diff_id);
    }

    if (
      Object.hasOwn(rawDiffResponse, "detail") &&
      typeof rawDiffResponse.detail === "string" &&
      rawDiffResponse.detail.includes("No Diff MV found")
    ) {
      handleCreateDiff();
    }
  }, [rawDiffResponse]);

  // Track the latest diff status from the status endpoint
  useEffect(() => {
    if (!diffStatusResponse) return;

    if (typeof diffStatusResponse === "string") {
      setDiffStatus(diffStatusResponse);
    } else if (diffStatusResponse.status) {
      setDiffStatus(diffStatusResponse.status);
    } else {
      setDiffStatus(JSON.stringify(diffStatusResponse));
    }
  }, [diffStatusResponse]);

  // When the diff job transitions to DONE, bump reloadKey to refetch the diff data
  useEffect(() => {
    if (diffStatus === "DONE") {
      setReloadKey((prev) => prev + 1);
    }
  }, [diffStatus]);

  const statusText = (() => {
    if (!createdDiffBuildId) return "Checking for diff data....";
    if (isCheckingStatus) return "Checking diff status...";
    if (!diffStatus) return "No status available yet.";
    if (isPendingStatus) {
      return `Diff #${createdDiffBuildId} In-Progress (Status: ${diffStatus})`;
    }
    if (diffStatus === "DONE") return "Diff complete. Loading results...";
    if (isErrorStatus) return `Diff job error (Status: ${diffStatus}).`;
    return `Status: ${diffStatus}`;
  })();

  //validation bar end states- these do not really display to the user unless something is really laggy or wrong since this component gets hidden when data returns, but good to have
  const progressValidation = (() => {
    if (isErrorStatus) {
      return { type: "error", message: "Error Creating Diff" };
    }
    if (diffStatus === "DONE") {
      return { type: "success", message: "Diff Complete!" };
    }
    return undefined;
  })();

  //bool for whether the progress bar should be indeterminate (spinning) or determinate (progressed to 100%)
  const isIndeterminate = (() => {
    if (isPendingStatus) return true; // WAITING or INPROGRESS
    if (diffStatus === "DONE" || isErrorStatus) return false; // DONE, FAILED, DEPRECATED
    // For initial/unknown states, keep it indeterminate
    return true;
  })();

  const columns = useMemo(
    () => [
      {
        header: "Change Type",
        accessorKey: "changeType",
      },
      {
        header: "Client_DS_ID",
        accessorKey: "clientDsId",
      },
      {
        header: "DataSrc",
        accessorKey: "dataSrc",
      },
      {
        header: "DCC_N",
        accessorKey: "dccN",
      },
      {
        header: "DCC_O",
        accessorKey: "dccO",
      },
      {
        header: "GenericName_N",
        accessorKey: "genericNameN",
      },
      {
        header: "GenericName_O",
        accessorKey: "genericNameO",
      },
      {
        header: "Group ID",
        accessorKey: "groupId",
      },
      {
        header: "HGMK_N",
        accessorKey: "hgmkN",
      },
      {
        header: "HGMK_O",
        accessorKey: "hgmkO",
      },
      {
        header: "HMKN",
        accessorKey: "hmkN",
      },
      {
        header: "HMKO",
        accessorKey: "hmkO",
      },
      {
        header: "Local Description",
        accessorKey: "localDescription",
      },
      {
        header: "Local Med Code",
        accessorKey: "localMedCode",
      },
      {
        header: "mapUsed_N",
        accessorKey: "mapUsedN",
      },
      {
        header: "mapUsed_O",
        accessorKey: "mapUsedO",
      },
      {
        header: "Num_Recs",
        accessorKey: "numRecs",
      },
      {
        header: "Priority_N",
        accessorKey: "priorityN",
      },
      {
        header: "Priority_O",
        accessorKey: "priorityO",
      },
      {
        header: "Review Required",
        accessorKey: "reviewRequired",
      },
    ],
    []
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  // Interpret medDomainBuildDiffData as a three-state value:
  // null  -> not loaded yet / loading
  // []    -> loaded but no rows
  // [...] -> loaded with rows
  const rawData = medDomainBuildDiffData;
  const isNotLoadedYet = rawData === null;
  const hasDiffRows = Array.isArray(rawData) && rawData.length > 0;
  const isLoadedEmpty = Array.isArray(rawData) && rawData.length === 0;

  // Always provide an array to the table hooks/components
  const data = Array.isArray(rawData) ? rawData : [];

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
    tableConfig: {
      enableColumnFilters: true,
      enableSorting: true,
      state: {
        pagination,
      },
      onPaginationChange: setPagination,
    },
    paginationConfig: {
      enablePagination: true,
      pageSizeOptions: [100, 200, 300],
    },
  });

  useEffect(() => {
    dataTableProps.setData(data);
  }, [data, dataTableProps.setData]);

  const diffTitle = `Diff Results - ${groupId}, (ID ${newBuildId} & ID ${oldBuildId})`;

  return (
    <Layout>
      <div className="row m-3">
        <h3>MedDomain Diff</h3>
        <Breadcrumbs
          items={[
            { title: "Home", href: "/" },
            { title: PATHS.medDomain.displayName, href: PATHS.medDomain.url },
            { title: "MedDomain Diff", href: currentUrl },
          ]}
        />
      </div>

      {(isNotLoadedYet || isPendingStatus) && ( //show progress bar if we're still loading the diff data OR if we know the diff job is still running
        <div className="row m-5">
          <div className="mt-2">
            <ProgressBar
              label={statusText}
              isIndeterminate={isIndeterminate}
              validation={progressValidation}
              percentage={isPendingStatus ? undefined : 100}
            />
            <p>
              Status will update automatically. Table will load once diff is
              complete.
            </p>
          </div>
        </div>
      )}

      {(hasDiffRows || (isLoadedEmpty && diffStatus === "DONE")) && ( //show the table if we have rows OR if we know for sure we have no rows (i.e., loaded empty)
        <div className="row mb-5">
          <DataTable title={diffTitle} tableState={dataTableProps}>
            <DataTable.Pagination
              variant="extended"
              showPageSizeDropdown={true}
            />
            <DataTable.Table />
          </DataTable>
        </div>
      )}
    </Layout>
  );
}
